// @flow strict

import { useDispatch } from 'react-redux';
import invariant from 'invariant';
import { all, put } from 'typed-redux-saga/macro';

import type { Action } from 'Lib/reduxType';

import { type OneOfAppPlatformEventValue } from 'EventModels/app/platformBuilder';
import { trackEvent, trackPlatformAppEvent } from 'Store/sparks/actions';
import type { AnalyticsEventActionType } from 'Store/sparks/reporter';

import { logHubbleEvent } from './trackers/hubble/logging';

export type GetPlatformTrackerData<TData> = (TData) => [
  OneOfAppPlatformEventValue,
  [string, Object] | void
];
export type EventActionCreatorFn<TData> = (TData) => Array<AnalyticsEventActionType | Action>;
export type EventPayloadFunctions<TData> = {| platformPayloadFn: GetPlatformTrackerData<TData> |};

/**
 * Creates an TrackAppEvent action from a payloadFn.
 * Argument payloadFn maps input of type TData (user defined) to the generated type
 * OneOfAppFeatureEventValue or OneOfAppPlatformEventValue and optional legacy track event arguments.
 * In the payloadFn, perform any translation from legacy types to generated protobuf enums.
 */
export function createEventActionCreator<TData>(
  trackerFunctions: EventPayloadFunctions<TData>
): EventActionCreatorFn<TData> {
  return function createEventAction(data: TData) {
    const payloadFn = trackerFunctions.platformPayloadFn;
    const trackMethod = trackPlatformAppEvent;

    invariant(payloadFn, 'createEventAction() requires platformPayloadFn');

    const [eventData, legacyEventData] = payloadFn(data);
    return [
      trackMethod((eventData: any)),
      legacyEventData ? trackEvent(...legacyEventData) : undefined,
    ].filter(Boolean);
  };
}

/**
 * Creates a wrapper to return all put actions for an event tracker.
 * Use in: Sagas
 */
export function createPutTrackerAction<TData>(
  createActionFn: EventActionCreatorFn<TData>
): (TData) => Function {
  return function putTrackAllAction(data: TData): Function {
    const [eventAction, legacyEventAction] = createActionFn(data);
    return all([put(eventAction), put(legacyEventAction)]);
  };
}

/**
 * Creates an event tracker from an EventActionCreator
 * The event tracker accepts dispatch and type TData as props and handles calling dispatch with the actions.
 * Use in: React Function Component Callbacks (prefer createTrackerHook if possible), React Class Components,
 * Non-React Classes/Services
 * NOTE: This handles legacy event actions which will soon be deprecated.
 */
export function createTracker<TData>(
  createActionFn: EventActionCreatorFn<TData>
): ((Action) => void, TData) => void {
  return function dispatchEvents(dispatch: (Action) => void, data: TData): void {
    const actions = createActionFn(data);
    actions.forEach((action) => {
      logHubbleEvent(action);
      dispatch(action);
    });
  };
}

/**
 * Creates an event tracker from an EventActionCreator
 * The event tracker accepts type TData as props and returns the appropriate action.
 * Use in: React Function Component Callbacks (prefer createTrackerHook if possible), React Class Components,
 * Non-React Classes/Services
 */
export function createEventTracker<TData>(
  createActionFn: EventActionCreatorFn<TData>
): (TData) => AnalyticsEventActionType {
  return function dispatchEvents(data: TData): AnalyticsEventActionType {
    const [action] = createActionFn(data);
    invariant(action, 'EventActionCreatorFn must return a trackEvent action.');
    logHubbleEvent(action);
    return action;
  };
}

/**
 * Creates an event tracker hook from an EventActionCreator
 * The event tracker hook accepts type TData as props and handles calling dispatch with the actions.
 * Use In: React Function components
 */
export function createTrackerHook<TData>(
  createPayloadFn: EventActionCreatorFn<TData>
): (data: TData) => void {
  const tracker = createTracker<TData>(createPayloadFn);
  return function useTrackEvent(data: TData) {
    const dispatch = useDispatch();
    tracker(dispatch, data);
  };
}
